/** Canonical pure semantics, shared by the host and native editor. */
import { CUT_HARD_MIN_SEC, CUT_MIN_SEC, STORY_CUT_MAX_SEC, cutDurationBounds, cutMaxSecForStory } from '../cut-duration';


/**
 * ✏️ 자막 하한 안내의 **문구 SSOT** — 토스트와 패널이 같은 말을 하게 한다.
 * ★왜 조각으로 돌려주나★: 토스트는 문자열 하나여야 하고(버튼을 못 담는다) 패널은 [직접 수정] 을
 *   **진짜 딥링크 버튼**으로 그려야 한다(막다른 길 금지 — 정리하라면서 창구를 안 열어 주면 안 된다).
 *   두 곳이 각자 문구를 적으면 갈린다. 그래서 조각 하나를 두 형상이 나눠 쓴다.
 */
export function sceneSpecCueNoticeParts(
  cueEndSec: number,
  minSec: number,
): { before: string; link: string; after: string; plain: string } {
  /* ★표시 수치는 **올림**이다★(적대 1R P3) — round1 로 내리면 6.02초 자막이 "6초까지 있어요"인데
     하한은 ceil(6.02)=7 이라 6 을 거절한다(화면이 허락한 값을 화면이 거절 = 유령 입력의 얼굴).
     올림하면 "6.1초까지 있어요 · 최소 7초"라 두 수치가 서로를 반박하지 않는다. */
  const shown = Math.ceil(cueEndSec * 10) / 10;
  const head = `이 씬 자막이 ${shown}초까지 있어요 — 최소 ${minSec}초예요. `;
  return {
    before: head,
    /* ★링크 문구가 곧 **할 수 있는 일**이다★(세호 F09 판정) — "직접 수정 →"은 목적지 이름일 뿐이라
       눌러서 뭐가 좋아지는지를 말하지 않는다. "자막을 줄이면 더 내려가요"는 이 벽이 **넘을 수 있는
       벽**임을 알려 준다(규칙 하한과 갈리는 지점이 정확히 이것이다). */
    link: "자막을 줄이면 더 내려가요 →",
    after: "",
    /* 토스트용 — ★대괄호 링크 표기를 쓰지 않는다★: 문자열 안의 버튼 표기는 누를 수 없어
       "버튼처럼 생겼는데 안 눌리는" 막다른 길이 된다. 창구는 패널의 진짜 버튼이 연다. */
    plain: `${head}직접 수정에서 자막을 줄이면 더 내려가요`,
  };
}


/**
 * 🧱 **못 줄이는 이유는 둘이고, 둘은 성질이 다르다**(세호 F09 판정 2026-08-19).
 *
 * ★규칙 하한(대사 유무 4/5·음성 하한)은 **넘을 수 없는 벽**이다★ — 유저가 지금 할 수 있는 일이
 *   없다. 그런 문구에 [직접 수정] 딥링크를 붙이면 "거기 가면 풀린다"는 거짓 약속이 되고, 가 봐야
 *   아무것도 못 한다(막다른 길 금지 헌법이 막는 바로 그 형상).
 * ★자막 하한은 **넘을 수 있는 벽**이다★ — 자막을 줄이면 실제로 내려간다. 그래서 그쪽만 창구를 연다.
 * ★상한 사유는 붙이지 않는다★ — "못 줄인다"를 설명하는 자리에 "더 못 늘린다"가 따라오면 두 벽이
 *   한 문장에 섞여 어느 쪽에 막힌 건지 알 수 없다.
 */
export function floorBlockNotice(
  cueDrivesFloor: boolean,
  cueEndSec: number,
  voiceFloorSec: number,
  voiceSynced: boolean,
  minSec: number,
): string {
  if (cueDrivesFloor) return sceneSpecCueNoticeParts(cueEndSec, minSec).plain;
  if (voiceFloorSec > 0) return sceneSpecVoiceFloorNoticeParts(voiceFloorSec, minSec).plain;
  if (voiceSynced) return `대사가 있는 씬은 ${minSec}초까지만 줄일 수 있어요`;
  return `${minSec}초까지만 줄일 수 있어요`;
}


/**
 * 🗣 **음성 하한 문구의 조각 — W4 대본 탭 딥링크 자리를 예약해 둔다**(F09 ③ · 이슈 #2487 §3).
 *
 * ★지금은 링크가 **비어 있다**★: W2/W3 에는 대본을 줄일 수 있는 창구가 없다. 그런데 이 벽은
 *   자막 하한과 달리 **넘을 수 있는 벽**이다 — 대본이 짧아지면 음성이 짧아지고 하한이 실제로 내려간다.
 *   그래서 "규칙 하한 = 넘을 수 없는 벽"으로 뭉뚱그려 두면 W4 착지 때 이 갈래를 다시 찾아야 한다.
 *   조각 함수로 형상을 미리 세워 두고 **`link` 만 비운다** — 빈 링크는 화면에서 아무것도 그리지 않으므로
 *   지금 거동은 1비트도 안 바뀌고(막다른 길 0), 착지 때는 이 한 줄만 채우면 된다.
 * // TODO(W4 대본 탭): 딥링크 "대본을 줄이면 더 내려갑니다 →" — 목적지 = 이 씬의 대본 탭(F17).
 * //   완성 문구: `이 씬 음성이 N초라 M초까지만 줄일 수 있어요 — 대본을 줄이면 더 내려갑니다 [대본 수정 →]`
 */
export function sceneSpecVoiceFloorNoticeParts(
  voiceFloorSec: number,
  minSec: number,
): { before: string; link: string; after: string; plain: string } {
  const head = `이 씬 음성이 ${round1(voiceFloorSec)}초라 ${minSec}초까지만 줄일 수 있어요`;
  return {
    before: head,
    /* ★W4 예약 슬롯 — 지금은 빈 값이다★. 채우는 순간 `after` 도 같이 채워야 문장이 성립한다
       (`before` + [link] + `after`). 값이 비어 있는 동안 화면은 링크를 그리지 않는다. */
    link: "",
    after: "",
    plain: head,
  };
}


export const cueNoticeText = (cueEndSec: number, minSec: number): string =>
  sceneSpecCueNoticeParts(cueEndSec, minSec).plain;


export interface SceneSpecDurationInput {
  /** 이 컷의 영상 모델(sedeo 카탈로그 키) — 상한의 원천. */
  model?: string | null;
  /**
   * ★생성 직전 음성 길이 동기화가 도는가★ — cut-duration 의 `voiceSynced` 계약을 그대로 옮긴다
   * (`!lockDuration && (대본 있음 || 음성 있음)`). 이 갈림이 하한 4↔5 를 가른다.
   */
  voiceSynced?: boolean;
  /**
   * 이 컷에 구워진 음성 길이(초) — 있으면 물리 하한이 그만큼 올라간다.
   *
   * ★현재 유일한 소비자(AgentStudio 씬 설정 시트)는 **언제나 null 을 넘긴다**(적대 리뷰 P3-16).
   *   에이전트 story 세그에는 씬별 음성 길이가 없다 — TTS 는 합본 트랙 하나로 굽는다. 그래서 아래
   *   `voiceFloorSec > 0` 분기는 지금 도달하지 않는 경로다. 지우지 않는 이유는 ⓐ`cutDurationBounds`
   *   가 이미 그 축을 계산해 주고 ⓑ씬별 음성 길이 축이 생기는 순간 여기만 채우면 되기 때문이다.
   *   ★도달하지 않는다는 사실을 적어 두는 것이 계약이다★ — 안 그러면 다음 사람이 "화면이 실측
   *   하한을 내걸고 있다"고 믿는다(가드의 시체를 일하는 가드로 오독).
   * ★혼동 금지 — `voiceSynced`(대사 유무)와는 다른 축이다★(F09 판정 이후): 대사 **유무**는
   *   `sceneHasSpokenLines` 가 자막 줄로 판정해 하한 4↔5 를 실제로 가른다(그 축은 이제 살아 있다).
   *   여기서 여전히 없는 것은 대사의 **길이**(몇 초짜리 발화인가)뿐이고, 그건 별건으로 남는다.
   */
  audioSec?: number | null;
  /**
   * ✏️ 이 씬 안 마지막 자막 cue 종료 시각(씬 시작 기준 상대 초) — `sceneLastCueEndSec` 의 값.
   * ★음성 축(`audioSec`)과 달리 이 축은 **실제로 살아 있다**★: 자막 타이밍은 세그에 실려 있다.
   *   없거나 0 이면 종전 규칙 하한 그대로(하위 호환 0 변화).
   */
  lastCueEndSec?: number | null;
}


export interface SceneSpecDurationBounds {
  minSec: number;
  maxSec: number;
  /** 하한·상한이 왜 그 값인지 — **누르기 전에** 보여 줄 문구(에러 패턴 제거 · 목업 대조 5). */
  reason: string;
  /**
   * ✏️ 위 `reason` 에서 **자막 cue 항만 뺀 것** — 패널은 cue 항을 딥링크 버튼이 박힌 문단으로
   * 따로 그리기 때문에(문자열로는 버튼을 못 담는다) 나머지를 그대로 이어 붙일 조각이 필요하다.
   * cue 가 하한을 정하지 않으면 `reason` 과 같다.
   */
  reasonWithoutCue: string;
  /**
   * 🧱 **"더 못 줄인다"의 사유 — 규칙 하한과 자막 하한을 갈라 말한다**(세호 F09 판정 2026-08-19).
   * ★`reason`(패널 설명)과 다른 문자열이다★: 설명은 "지금 범위가 왜 이런가"를 말하고, 이쪽은
   *   "지금 누른 것이 왜 안 되는가"를 말한다. 후자에는 **상한 사유를 섞지 않고**(어느 벽에 막혔는지
   *   흐려진다) 넘을 수 있는 벽(자막)일 때만 창구를 가리킨다(넘을 수 없는 벽에 딥링크 = 거짓 약속).
   */
  floorBlockedReason: string;
  /** ✏️ 자막 cue 가 끌어올린 하한(초·ceil). **0 = cue 축이 하한을 정하지 않았다**(문구도 없다). */
  cueFloorSec: number;
  /** 그 판정의 원천 실측치(초·반올림 전) — 문구가 말하는 수치. cueFloorSec 0 이면 0. */
  cueEndSec: number;
  /** ✏️ 자막이 상한보다 길다 = 어떤 값을 골라도 자막이 잘린다(씬을 나누거나 자막을 정리해야 한다). */
  cueExceedsCap: boolean;
  /** 음성이 상한보다 길다 = 어떤 값을 골라도 발화가 잘린다(컷을 나눠야 한다). */
  voiceExceedsCap: boolean;
  /**
   * 🎚 **화질을 올리면 닿을 수 있는 상한**(초) — 지금 상한이 모델 때문에 낮을 때만 채워진다.
   * ★숫자를 말하는 것이 요점이다★(세호님 F09 ②): 화질 전환은 작품 전체에 걸리는 **비용 조작**이라
   *   "고급으로 바꾸면 늘어나요"만으로는 결정할 수 없다 — 얼마나 늘어나는지를 알아야 한다.
   * ★상시 노출은 안 한다★ — 벽에 닿았을 때만(아래 stepSceneSpecDuration·시트 참조).
   * 이미 story 레인 천장이면 `null`(올릴 여지가 없는데 권하면 그게 막다른 길이다).
   */
  upgradeMaxSec: number | null;
}


/**
 * ⏱ 시트가 내걸 수 있는 길이 범위 — **판정은 전부 `cut-duration` SSOT 에서 읽는다**.
 * ★상한은 story 레인 캡(`cutMaxSecForStory`)★: 제작이 15로 구운 슬롯을 재생성만 30으로 요청하면
 *   합본 슬롯이 어긋난다(cut-duration STORY_CUT_MAX_SEC 주석이 "씬 1개 재생성"을 명시 대상으로 든다).
 */
export function sceneSpecDurationBounds(input: SceneSpecDurationInput): SceneSpecDurationBounds {
  const raw = cutDurationBounds({
    model: input.model,
    voiceSynced: input.voiceSynced,
    audioSec: input.audioSec,
  });
  const maxSec = Math.min(raw.maxSec, cutMaxSecForStory(input.model));
  /* ✏️ **자막 cue 하한**(수희 02 확정) — `ceil` 은 cut-duration 의 물리 하한 관용구와 동조한다
     (`voiceFloorSec` 이 ceil(음성+pad)로 정수 초를 내는 것과 같은 축: 컷 길이는 정수 초다).
     5.4초까지 자막이 있는데 5초로 확정하면 0.4초 분량 cue 가 씬 밖에 남는다 → 6초가 하한이다. */
  const cueEndRaw = Number(input.lastCueEndSec);
  const cueEndSec = Number.isFinite(cueEndRaw) && cueEndRaw > 0 ? cueEndRaw : 0;
  const cueFloorRaw = cueEndSec > 0 ? Math.ceil(cueEndSec) : 0;
  /* ✏️ cue 가 **규칙 하한을 실제로 끌어올릴 때만** 그 축이 하한을 "정한" 것이다. 규칙 하한 이하면
     이미 규칙이 그 자막을 품고 있어 새로 말할 것이 없다(같은 값이면 사유도 늘리지 않는다). */
  const cueDrivesFloor = cueFloorRaw > raw.minSec;
  /* ✏️ 자막이 상한보다 길다 = 어떤 값을 골라도 잘린다. 하한은 상한으로 접히지만(아래) 그 사실을
     삼키지 않고 플래그로 내보낸다 — `voiceExceedsCap` 과 같은 형상. */
  const cueExceedsCap = cueFloorRaw > maxSec;
  // 하한이 상한을 넘으면 접는다 — min>max 인 입력창은 어떤 값도 유효하지 않아 그 자체로 막다른 길이다.
  const minSec = Math.min(Math.max(raw.minSec, cueFloorRaw), maxSec);
  /* ★하한 사유는 **하나만** 뜬다★ — 실측 하한을 아는 경우·자막 cue 가 정한 경우·규칙 기본값은 서로
     다른 조건이라 함께 뜨면 자기모순이다("자막이 5.4초까지 있어요"와 "대사가 있어 최소 5초"가 나란히
     서면 화면이 스스로 5초를 고를 수 있다고 거짓말한다). cue 가 이겼으면 cue 가 그 자리를 말한다. */
  const reasons: string[] = [];
  if (cueDrivesFloor) reasons.push(cueNoticeText(cueEndSec, minSec));
  else if (raw.voiceFloorSec > 0) reasons.push(`이 씬 음성이 ${round1(raw.voiceFloorSec)}초라 최소 ${minSec}초`);
  else if (input.voiceSynced) reasons.push(`대사가 있어 최소 ${CUT_MIN_SEC}초`);
  else reasons.push(`최소 ${CUT_HARD_MIN_SEC}초`);
  reasons.push(`이 작업물 화질 기준 최대 ${maxSec}초`);
  /* 🎚 지금 상한이 **모델 때문에** story 레인 천장보다 낮은가 — 그렇다면 화질을 올려 닿을 수 있는
     값은 그 천장이다(하드코딩 금지: 두 값 다 cut-duration SSOT 에서 읽는다). */
  const upgradeMaxSec = maxSec < STORY_CUT_MAX_SEC ? STORY_CUT_MAX_SEC : null;
  /* ★모르는 것을 아는 척하지 않는다★ — 에이전트 story 세그에는 이 씬 음성의 실측 길이가 실려 있지
     않다(TTS 는 합본 트랙 하나로 구워진다). 그래서 대사 씬의 하한은 "5초"라는 **일반 규칙**일 뿐
     이 씬의 물리 하한이 아니고, 실제로는 생성 직전 resolveCutDuration 이 발화 길이만큼 올려 잡는다.
     이 한 줄이 없으면 6초를 골랐는데 9초로 나오는 것이 그대로 #1331 의 "유령 입력"이 된다. */
  if (input.voiceSynced && (input.audioSec == null || !(input.audioSec > 0))) {
    reasons.push("음성이 더 길면 생성 때 그만큼 늘어나요");
  }
  return {
    minSec,
    maxSec,
    reason: reasons.join(" · "),
    // cue 항은 언제나 맨 앞이라(위 분기) 뺀 나머지는 slice(1) 이다. 안 이겼으면 뺄 것이 없다.
    reasonWithoutCue: (cueDrivesFloor ? reasons.slice(1) : reasons).join(" · "),
    floorBlockedReason: floorBlockNotice(cueDrivesFloor, cueEndSec, raw.voiceFloorSec, !!input.voiceSynced, minSec),
    cueFloorSec: cueDrivesFloor ? cueFloorRaw : 0,
    cueEndSec: cueDrivesFloor ? cueEndSec : 0,
    cueExceedsCap,
    voiceExceedsCap: raw.voiceExceedsCap,
    upgradeMaxSec,
  };
}


export const round1 = (v: number) => Math.round(v * 10) / 10;


/**
 * 🚫 **확정(저장·발주) 차단 판정** — 경계 밖 값이면 사유, 유효하면 `null`.
 *
 * ★왜 clamp 가 아니라 차단인가★(수희 02 확정): 하한 밑 값을 조용히 하한으로 올려 확정하면 유저가
 *   고른 것과 다른 길이가 구워진다(#1331 유령 입력). 반대로 그대로 확정하면 **씬 끝을 넘는 cue 가
 *   데이터에 남아 SRT 로 샌다**. 둘 다 안 되므로 남는 답은 하나 — 확정 자체를 막고 정리 창구를
 *   가리킨다. 스테퍼는 이미 사전 차단이라, 여기 걸리는 건 구버전 캡슐·경합 회차뿐이다(fail-closed 2중선).
 * ★"자동"(null)은 막지 않는다★ — 값을 안 고른 것이라 경계 위반이 성립하지 않고, 생성 직전
 *   resolveCutDuration 이 제 하한을 적용한다.
 */
export function sceneSpecDurationBlocked(
  durationSec: number | null | undefined,
  bounds: SceneSpecDurationBounds,
): string | null {
  if (typeof durationSec !== "number" || !Number.isFinite(durationSec) || durationSec <= 0) return null;
  // ★하한 사유도 갈라 말한다★(세호 F09) — 규칙 벽이면 창구를 가리키지 않고, 자막 벽이면 가리킨다.
  if (durationSec < bounds.minSec) return `재생성할 수 없어요 — ${bounds.floorBlockedReason}`;
  // ★상한 문구에는 자막 사유를 싣지 않는다★ — 자막은 하한 축이라 늘리기를 막는 이유가 아니다.
  if (durationSec > bounds.maxSec) return `${bounds.maxSec}초보다 길게는 만들 수 없어요 — ${bounds.reasonWithoutCue}`;
  return null;
}
